import React, { useEffect, useRef, useState } from "react";
import { AppLayout } from "src/components/layouts";
import LogisticTable from "./components/LogisticTable";
import LogisticsAnalysis from "./components/LogisticsAnalysis";
import AssignRider from "./components/AssignRider"; // Import AssignRider component
import AdminService from "src/services/admin.services";
import { Logistic } from "src/services/types";
import BackButton from "src/components/BackButton";
import FoodLoader from "src/components/Loader";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "react-query";
import { NIGERIAN_STATES } from "../../utils/constants";
import CustomSelect from "../../components/ui/CustomSelect";

const adminService = new AdminService();
const ITEMS_PER_PAGE = 100; // Adjusted to 40 per the API requirement

const LogisticsManagement: React.FC = () => {
  const [data, setData] = useState<Logistic[]>([]);
  const [, setError] = useState<string | null>(null);
  const [filteredData, setFilteredData] = useState<Logistic[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [activeTab, setActiveTab] = useState<
    "table" | "analysis" | "assignRider"
  >("table"); // Add AssignRider tab
  const [searching, setSearching] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedState, setSelectedState] = useState<string>("all");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedOrder, setSelectedOrder] = useState<Logistic | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");

  const {
    data: logisticsData,
    refetch: refetchSelectedOrder,
    isLoading,
    isError,
    error,
  } = useQuery(
    ["logistics-management", currentPage],
    () =>
      adminService.getLogisticsManagement(
        currentPage,
        8.6753,
        9.082,
        100000000000000n,
        "",
        ""
      ),
    {
      refetchInterval: 30000,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      enabled: !searching && !selectedOrder && !orderId,
      onSuccess: (data) => {
        setData(data);
        if (!searching) setFilteredData(data);
      },
      onError: (error) => {
        setError(`Error fetching logistics data: ${error}`);
      },
    }
  );
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

  return (
    <AppLayout>
      <div className="container mx-auto px-2 py-4">
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm p-2 overflow-x-hidden">
            {/* Top Section */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
              <div className="flex items-center gap-4">
                <BackButton />
                <h2 className="text-2xl font-bold text-amber-900">
                  Logistics Management
                </h2>
              </div>

              {/* Tabs */}
              <div className="flex flex-wrap gap-2">
                <button
                  className={`py-2 px-4 rounded-md text-sm font-medium ${
                    activeTab === "table"
                      ? "bg-amber-500 text-white"
                      : "bg-gray-200 text-gray-700"
                  }`}
                  onClick={() => setActiveTab("table")}
                >
                  Table View
                </button>
                <button
                  className={`py-2 px-4 rounded-md text-sm font-medium ${
                    activeTab === "analysis"
                      ? "bg-amber-500 text-white"
                      : "bg-gray-200 text-gray-700"
                  }`}
                  onClick={() => setActiveTab("analysis")}
                >
                  Analysis View
                </button>
                <button
                  className={`py-2 px-4 rounded-md text-sm font-medium ${
                    activeTab === "assignRider"
                      ? "bg-amber-500 text-white"
                      : "bg-gray-200 text-gray-700"
                  }`}
                  onClick={() => setActiveTab("assignRider")}
                >
                  Assign Rider
                </button>
              </div>
            </div>

            {/* Content */}
            {isLoading ? (
              <div className="h-[70vh] w-full bg-white flex flex-col items-center justify-center">
                <FoodLoader
                  size="md"
                  text="Fetching Logistics"
                  theme="amber"
                  className="scale-75"
                />
              </div>
            ) : error ? (
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-red-500"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    {/* <p className="text-sm text-red-700">{error}</p> */}
                  </div>
                </div>
              </div>
            ) : activeTab === "table" ? (
              <LogisticTable
                data={filteredData}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                itemsPerPage={ITEMS_PER_PAGE}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                setSearching={setSearching}
                setIsModalOpen={setIsModalOpen}
                selectedOrder={selectedOrder}
                setSelectedOrder={setSelectedOrder}
                setSearchParams={setSearchParams}
                searchParams={searchParams}
                orderId={orderId}
                refetchSelectedOrder={refetchSelectedOrder}
                selectedState={selectedState}
                setSelectedState={setSelectedState}
                nigerianStates={NIGERIAN_STATES}
              />
            ) : activeTab === "analysis" ? (
              <LogisticsAnalysis logistics={filteredData} />
            ) : (
              <AssignRider />
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default LogisticsManagement;
